// src/auth/AuthContext.js
import { createContext, useState, useEffect } from "react";
import api from "../api/client";

export const AuthContext = createContext();

export function AuthProvider({ children }) {

  const [token,         setToken]         = useState(localStorage.getItem("token"));
  const [orgId,         setOrgId]         = useState(localStorage.getItem("orgId"));
  const [departmentId,  setDepartmentId]  = useState(localStorage.getItem("departmentId"));
  const [user,          setUser]          = useState(null);
  const [phoneVerified, setPhoneVerified] = useState(() => {
    const v = localStorage.getItem("phoneVerified");
    return v === null ? null : v === "true";
  });

  // True until the initial "is this token still good" check resolves.
  // Routing decisions (PhoneVerifiedRoute etc.) must wait on this instead
  // of trusting localStorage at face value.
  const [isInitializing, setIsInitializing] = useState(true);

  // Runs once on mount. If there's a token in localStorage, verify it
  // against the backend before letting any route render. This replaces
  // the old "fire me/ in a useEffect and hope" approach, which let stale
  // sessions render protected routes for a tick before failing.
  useEffect(() => {
    const currentToken = localStorage.getItem("token");

    if (!currentToken) {
      setIsInitializing(false);
      return;
    }

    api.get("me/")
      .then(res => {
        setUser(res.data);
        // Trust the server's view of phoneVerified over whatever was cached,
        // if the endpoint returns it. Falls back to cached value otherwise.
        if (res.data && typeof res.data.phone_verified === "boolean") {
          localStorage.setItem("phoneVerified", String(res.data.phone_verified));
          setPhoneVerified(res.data.phone_verified);
        }
      })
      .catch(() => {
        // me/ failing here means the token is dead. The response
        // interceptor in client.js will also clear storage and redirect
        // on the 401, but we clear local state immediately too so this
        // provider doesn't keep claiming the user is authenticated while
        // that redirect is in flight.
        localStorage.clear();
        setToken(null);
        setOrgId(null);
        setDepartmentId(null);
        setPhoneVerified(null);
        setUser(null);
      })
      .finally(() => {
        setIsInitializing(false);
      });
  }, []);

  const login = (token, phoneVerifiedFlag, refreshToken) => {
    localStorage.setItem("token", token);
    setToken(token);

    if (refreshToken) {
      localStorage.setItem("refresh_token", refreshToken);
    }

    if (phoneVerifiedFlag !== undefined) {
      localStorage.setItem("phoneVerified", String(phoneVerifiedFlag));
      setPhoneVerified(phoneVerifiedFlag);
    } else {
      localStorage.removeItem("phoneVerified");
      setPhoneVerified(null);
    }
  };

  const markPhoneVerified = () => {
    localStorage.setItem("phoneVerified", "true");
    setPhoneVerified(true);
  };

  const selectOrg = (orgId) => {
    localStorage.setItem("orgId", orgId);
    setOrgId(orgId);
    localStorage.removeItem("departmentId");
    setDepartmentId(null);
  };

  const selectDepartment = (departmentId) => {
    localStorage.setItem("departmentId", departmentId);
    setDepartmentId(departmentId);
  };

  const logout = () => {
    localStorage.clear();
    setToken(null);
    setOrgId(null);
    setDepartmentId(null);
    setPhoneVerified(null);
    setUser(null);
  };

  const needsPhoneVerification = phoneVerified === false;

  return (
    <AuthContext.Provider value={{
      token,
      orgId,
      departmentId,
      user,
      phoneVerified,
      needsPhoneVerification,
      isInitializing,
      login,
      markPhoneVerified,
      selectOrg,
      selectDepartment,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}